import "./SideMenu.scss";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MotifVerticalNavigationContent,
  MotifVerticalNavigationMenu,
  MotifVerticalNavigationMenuItem,
  MotifVerticalNavigation,
} from "@ey-xd/motif-react";
import { routes } from "../../routes/routes.constatns";
import { TransitionOpen } from "../../assets/Icons/TransitionOpen";
import { TransitionClose } from "../../assets/Icons/TransitionClose";
import { HomeIcon } from "../../assets/Icons/HomeIcon";
import { ListIcon } from "../../assets/Icons/ListIcon";
import { ReportsIcon } from "../../assets/Icons/Reports";
import { MenuIcon } from "../../assets/Icons/MenuIcon";
import { UserPlus } from "../../assets/Icons/UserPlus";
import { SIDE_MENU } from "../../util/constants";
import { clearCurrentOrder, triggerOrdersRefresh } from "../../store/slices/ordersSlice";

// Vertical navigation sidebar with menu items and collapsible history panel
export function SideMenu({ isSideMenuCollapsed, setSideMenuCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userRoles } = useSelector((state) => state.user);
  const [activeMenuItem, setActiveMenuItem] = useState("home");
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);

  // Check if user has Admin role in any of their roles (case-insensitive)
  const isAdmin = Array.isArray(userRoles) && userRoles.some((role) => {
    const roleName = typeof role === 'object' ? (role.roleName || role.name) : role;
    return roleName?.toLowerCase() === 'admin' && role.status !== false; // Ensure admin role is active
  });

  // Check if all non-admin roles are inactive
  const allRolesInactive = Array.isArray(userRoles) && userRoles.length > 0 && userRoles.every((role) => {
    const roleName = typeof role === 'object' ? (role.roleName || role.name) : role;
    const isActive = typeof role === 'object' ? role.status !== false : true;
    // Skip admin role when checking - only check non-admin roles
    if (roleName?.toLowerCase() === 'admin') return true;
    return !isActive;
  });

  // Check if user is admin-only (has only admin role or all non-admin roles are inactive)
  const isAdminOnly = Array.isArray(userRoles) && userRoles.length > 0 && userRoles.every((role) => {
    const roleName = typeof role === 'object' ? (role.roleName || role.name) : role;
    const isActive = typeof role === 'object' ? role.status !== false : true;
    // User is admin-only if all roles are either admin or inactive
    return roleName?.toLowerCase() === 'admin' || !isActive;
  });

  // Check if user has no roles at all
  const hasNoRoles = !Array.isArray(userRoles) || userRoles.length === 0;

  // Determine if menu items should be disabled (either all roles inactive, admin-only, or no roles)
  const shouldDisableMenuItems = allRolesInactive || isAdminOnly || hasNoRoles;

  // Update active menu item based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/" || path === "/home") {
      setActiveMenuItem("home");
    } else if (path.startsWith("/orders")) {
      setActiveMenuItem("orders");
    } else if (path.startsWith("/tasks")) {
      setActiveMenuItem("tasks");
    } else if (path.startsWith("/reports")) {
      setActiveMenuItem("reports");
    } else if (path.startsWith("/user-management")) {
      setActiveMenuItem("userManagement");
    }
  }, [location.pathname]);

  // Navigates to route and updates active menu state
  /* istanbul ignore next */
  const handleSideMenuClick = (route, menuItem) => {
    setActiveMenuItem(menuItem);
    // Clear current order state when navigating away from order details
    // This prevents stale order data from being used when opening a new order later
    if (menuItem === "orders" || menuItem === "home") {
      dispatch(clearCurrentOrder());
    }
    // Trigger fresh orders list fetch when navigating to orders page
    if (menuItem === "orders") {
      dispatch(triggerOrdersRefresh());
    }
    navigate(route);
  };

  // Toggles sidebar collapsed state
  const toggleSideMenu = () => {
    if (setSideMenuCollapsed) {
      setSideMenuCollapsed();
    }
  };

  // Toggles history panel visibility
  const toggleHistoryPanel = () => {
    setIsHistoryPanelOpen(!isHistoryPanelOpen);
  };

  return (
    <>
      <MotifVerticalNavigation
        collapse={isSideMenuCollapsed}
        className="app-side-menu"
      >
        <MotifVerticalNavigationContent>
          <MotifVerticalNavigationMenu>
            <MotifVerticalNavigationMenuItem
              icon={
                isSideMenuCollapsed ? <TransitionOpen /> : <TransitionClose />
              }
              label="OpenClose"
              onClick={toggleSideMenu}
              className={`${isSideMenuCollapsed ? "openClose" : ""}`}
            ></MotifVerticalNavigationMenuItem>

            <div className="sidemenu-separator"></div>

            <MotifVerticalNavigationMenuItem
              icon={<HomeIcon />}
              label={SIDE_MENU.HOME}
              onClick={() => !hasNoRoles && handleSideMenuClick(routes.home, "home")}
              className={`${activeMenuItem === "home" ? "active-menu-item" : ""} ${hasNoRoles ? "disabled-menu-item" : ""}`}
            >
              {SIDE_MENU.HOME}
            </MotifVerticalNavigationMenuItem>
            <MotifVerticalNavigationMenuItem
              icon={<ListIcon />}
              label={SIDE_MENU.MY_ORDERS}
              onClick={() =>
                !shouldDisableMenuItems && handleSideMenuClick(routes.orders || routes.home, "orders")
              }
              className={`${activeMenuItem === "orders" ? "active-menu-item" : ""} ${shouldDisableMenuItems ? "disabled-menu-item" : ""}`}
            >
              {SIDE_MENU.MY_ORDERS}
            </MotifVerticalNavigationMenuItem>
            <MotifVerticalNavigationMenuItem
              icon={<MenuIcon />}
              label={SIDE_MENU.MY_TASKS}
              onClick={() =>
                !shouldDisableMenuItems && handleSideMenuClick(routes.tasks || routes.home, "tasks")
              }
              className={`disabled-menu-item ${activeMenuItem === "tasks" ? "active-menu-item" : ""}`}
            >
              {SIDE_MENU.ALL_ORDERS}
            </MotifVerticalNavigationMenuItem>
            <MotifVerticalNavigationMenuItem
              icon={<ReportsIcon />}
              label={SIDE_MENU.REPORTS}
              className={`disabled-menu-item ${activeMenuItem === "reports" ? "active-menu-item" : ""}`}
            >
              {SIDE_MENU.REPORTS}
            </MotifVerticalNavigationMenuItem>
          </MotifVerticalNavigationMenu>
          {isAdmin && !hasNoRoles && (
            <MotifVerticalNavigationMenu>
              <MotifVerticalNavigationMenuItem
                icon={<UserPlus />}
                label={SIDE_MENU.USER_MANAGEMENT}
                onClick={() =>
                  handleSideMenuClick(routes.userManagement, "userManagement")
                }
                className={activeMenuItem === "userManagement" ? "active-menu-item" : ""}
              >
                {SIDE_MENU.USER_MANAGEMENT}
              </MotifVerticalNavigationMenuItem>
            </MotifVerticalNavigationMenu>
          )}
        </MotifVerticalNavigationContent>
      </MotifVerticalNavigation>

    </>
  );
}
